import { useState } from "react";
import { EntityTable, type Column } from "../components/EntityTable";
import { Modal } from "../components/Modal";
import { useAdmin } from "../context/AdminContext";
import type { z } from "zod";
import { messageStructureSchemas } from "@pineseed/spec";

type MessageReaction = z.infer<typeof messageStructureSchemas.MessageReaction>;
type MessageRead = z.infer<typeof messageStructureSchemas.MessageRead>;
type MessageReply = z.infer<typeof messageStructureSchemas.MessageReply>;

type View = 'reactions' | 'reads' | 'replies';

export function MessengerPanel() {
  const { messenger } = useAdmin();
  const [view, setView] = useState<View>('reactions');
  
  // Forms
  const [showReactionForm, setShowReactionForm] = useState(false);
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [showReadForm, setShowReadForm] = useState(false);
  
  // Lookup
  const [messageIdInput, setMessageIdInput] = useState('');
  const [participantIdInput, setParticipantIdInput] = useState('');

  const handleLoadReactions = async () => {
    if (!messageIdInput.trim()) return;
    await messenger.getMessageReactions(messageIdInput.trim());
  };

  const handleLoadReplies = async () => {
    if (!messageIdInput.trim()) return;
    await messenger.getMessageReplies(messageIdInput.trim());
  };

  const handleLoadReactionSummary = async () => {
    if (!messageIdInput.trim()) return;
    await messenger.getReactionSummary(messageIdInput.trim());
  };

  const handleGetReadStatus = async () => {
    if (!participantIdInput.trim() || !messageIdInput.trim()) return;
    await messenger.getMessageReadStatus(participantIdInput.trim(), messageIdInput.trim());
  };

  const reactionColumns: Column<MessageReaction>[] = [
    {
      key: 'messageId',
      label: 'Message ID',
      render: (r) => (
        <span className="font-mono text-sm">{r.messageId.substring(0, 8)}...</span>
      ),
    },
    {
      key: 'participantId',
      label: 'Participant',
      render: (r) => (
        <span className="font-mono text-sm">{r.participantId}</span>
      ),
    },
    {
      key: 'emoji',
      label: 'Emoji',
      render: (r) => (
        <span className="text-2xl">{r.emoji}</span>
      ),
    },
    {
      key: 'timestamp',
      label: 'Timestamp',
      render: (r) => (
        <span className="text-sm text-gray-600 dark:text-gray-400">
          {new Date(r.timestamp).toLocaleString()}
        </span>
      ),
    },
  ];

  const readColumns: Column<MessageRead>[] = [
    {
      key: 'messageId',
      label: 'Message ID',
      render: (r) => (
        <span className="font-mono text-sm">{r.messageId.substring(0, 8)}...</span>
      ),
    },
    {
      key: 'participantId',
      label: 'Participant',
      render: (r) => (
        <span className="font-mono text-sm">{r.participantId}</span>
      ),
    },
    {
      key: 'readAt',
      label: 'Read At',
      render: (r) => (
        <span className="text-sm text-gray-600 dark:text-gray-400">
          {new Date(r.firstReadAt).toLocaleString()}
        </span>
      ),
    },
  ];

  const replyColumns: Column<MessageReply>[] = [
    {
      key: 'originalMessageId',
      label: 'Original Message',
      render: (r) => (
        <span className="font-mono text-sm">{r.originalMessageId.substring(0, 8)}...</span>
      ),
    },
    {
      key: 'replyMessageId',
      label: 'Reply Message',
      render: (r) => (
        <span className="font-mono text-sm">{r.replyMessageId.substring(0, 8)}...</span>
      ),
    },
    {
      key: 'participantId',
      label: 'Participant',
      render: (r) => (
        <span className="font-mono text-sm">{r.participantId}</span>
      ),
    },
    {
      key: 'timestamp',
      label: 'Timestamp',
      render: (r) => (
        <span className="text-sm text-gray-600 dark:text-gray-400">
          {new Date(r.timestamp).toLocaleString()}
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Messenger
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Manage message interactions (reads, reactions, replies)
          </p>
        </div>
      </div>

      {/* View Selector */}
      <div className="flex gap-2 border-b border-gray-200 dark:border-gray-700">
        <button
          onClick={() => setView('reactions')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            view === 'reactions'
              ? 'border-blue-500 text-blue-600 dark:text-blue-400'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
          }`}
        >
          Reactions
        </button>
        <button
          onClick={() => setView('reads')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            view === 'reads'
              ? 'border-blue-500 text-blue-600 dark:text-blue-400'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
          }`}
        >
          Read Status
        </button>
        <button
          onClick={() => setView('replies')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            view === 'replies'
              ? 'border-blue-500 text-blue-600 dark:text-blue-400'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
          }`}
        >
          Replies
        </button>
      </div>

      {/* Message ID Lookup */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex gap-2">
          <input
            type="text"
            value={messageIdInput}
            onChange={(e) => setMessageIdInput(e.target.value)}
            placeholder="Enter message ID..."
            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
          />
          {view === 'reactions' && (
            <>
              <button
                onClick={handleLoadReactions}
                disabled={!messageIdInput.trim() || messenger.loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50"
              >
                Load Reactions
              </button>
              <button
                onClick={handleLoadReactionSummary}
                disabled={!messageIdInput.trim() || messenger.loading}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium disabled:opacity-50"
              >
                Summary
              </button>
              <button
                onClick={() => setShowReactionForm(true)}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
              >
                Add Reaction
              </button>
            </>
          )}
          {view === 'reads' && (
            <>
              <input
                type="text"
                value={participantIdInput}
                onChange={(e) => setParticipantIdInput(e.target.value)}
                placeholder="Participant ID..."
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
              />
              <button
                onClick={handleGetReadStatus}
                disabled={!messageIdInput.trim() || !participantIdInput.trim() || messenger.loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50"
              >
                Get Read Status
              </button>
              <button
                onClick={() => setShowReadForm(true)}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
              >
                Mark as Read
              </button>
            </>
          )}
          {view === 'replies' && (
            <>
              <button
                onClick={handleLoadReplies}
                disabled={!messageIdInput.trim() || messenger.loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50"
              >
                Load Replies
              </button>
              <button
                onClick={() => setShowReplyForm(true)}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
              >
                Add Reply
              </button>
            </>
          )}
        </div>
      </div>

      {/* Reactions View */}
      {view === 'reactions' && (
        <div className="space-y-4">
          {/* Reaction Summary */}
          {messageIdInput.trim() && messenger.reactionSummary[messageIdInput.trim()] && (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                Reaction Summary
              </h3>
              <div className="flex gap-4 flex-wrap">
                {Object.entries(messenger.reactionSummary[messageIdInput.trim()]).map(([emoji, count]) => (
                  <div key={emoji} className="flex items-center gap-2 bg-gray-50 dark:bg-gray-900 px-4 py-2 rounded-lg">
                    <span className="text-2xl">{emoji}</span>
                    <span className="font-semibold text-gray-900 dark:text-gray-100">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          <EntityTable
            data={messenger.reactions}
            columns={reactionColumns}
            onDelete={(reaction) => messenger.removeReaction(reaction.participantId, reaction.messageId)}
            loading={messenger.loading}
            emptyMessage="No reactions loaded. Enter a message ID and click 'Load Reactions'."
          />
        </div>
      )}

      {/* Read Status View */}
      {view === 'reads' && (
        <EntityTable
          data={messenger.readStatuses}
          columns={readColumns}
          loading={messenger.loading}
          emptyMessage="No read statuses. Mark messages as read to see them here."
        />
      )}

      {/* Replies View */}
      {view === 'replies' && (
        <EntityTable
          data={messenger.replies}
          columns={replyColumns}
          loading={messenger.loading}
          emptyMessage="No replies loaded. Enter a message ID and click 'Load Replies'."
        />
      )}

      {/* Add Reaction Modal */}
      <Modal
        isOpen={showReactionForm}
        onClose={() => setShowReactionForm(false)}
        title="Add Reaction"
        maxWidth="md"
      >
        <ReactionForm
          onSubmit={async (data) => {
            await messenger.addReaction(data);
            setShowReactionForm(false);
          }}
          onCancel={() => setShowReactionForm(false)}
        />
      </Modal>

      {/* Mark as Read Modal */}
      <Modal
        isOpen={showReadForm}
        onClose={() => setShowReadForm(false)}
        title="Mark Message as Read"
        maxWidth="md"
      >
        <ReadForm
          onSubmit={async (data) => {
            await messenger.markMessageAsRead(data);
            setShowReadForm(false);
          }}
          onCancel={() => setShowReadForm(false)}
        />
      </Modal>

      {/* Add Reply Modal */}
      <Modal
        isOpen={showReplyForm}
        onClose={() => setShowReplyForm(false)}
        title="Add Reply"
        maxWidth="md"
      >
        <ReplyForm
          onSubmit={async (data) => {
            await messenger.addReply(data);
            setShowReplyForm(false);
          }}
          onCancel={() => setShowReplyForm(false)}
        />
      </Modal>

      {/* Error Display */}
      {messenger.error && (
        <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-sm text-red-800 dark:text-red-200">{messenger.error}</p>
        </div>
      )}
    </div>
  );
}

// Reaction Form Component
function ReactionForm({
  onSubmit,
  onCancel,
}: {
  onSubmit: (data: { participantId: string; messageId: string; threadId: string; emoji: string }) => Promise<void>;
  onCancel: () => void;
}) {
  const [participantId, setParticipantId] = useState('');
  const [messageId, setMessageId] = useState('');
  const [threadId, setThreadId] = useState('');
  const [emoji, setEmoji] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await onSubmit({
        participantId: participantId.trim(),
        messageId: messageId.trim(),
        threadId: threadId.trim(),
        emoji: emoji.trim(),
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="p-6 space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Participant ID <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={participantId}
          onChange={(e) => setParticipantId(e.target.value)}
          required
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Message ID <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={messageId}
          onChange={(e) => setMessageId(e.target.value)}
          required
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Thread ID <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={threadId}
          onChange={(e) => setThreadId(e.target.value)}
          required
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Emoji <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={emoji}
          onChange={(e) => setEmoji(e.target.value)}
          required
          maxLength={10}
          placeholder="👍 😊 ❤️"
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
        />
      </div>

      <div className="flex justify-end gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={submitting}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {submitting ? 'Adding...' : 'Add Reaction'}
        </button>
      </div>
    </form>
  );
}

// Read Form Component
function ReadForm({
  onSubmit,
  onCancel,
}: {
  onSubmit: (data: { participantId: string; messageId: string; threadId: string }) => Promise<void>;
  onCancel: () => void;
}) {
  const [participantId, setParticipantId] = useState('');
  const [messageId, setMessageId] = useState('');
  const [threadId, setThreadId] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await onSubmit({
        participantId: participantId.trim(),
        messageId: messageId.trim(),
        threadId: threadId.trim(),
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="p-6 space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Participant ID <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={participantId}
          onChange={(e) => setParticipantId(e.target.value)}
          required
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Message ID <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={messageId}
          onChange={(e) => setMessageId(e.target.value)}
          required
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Thread ID <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={threadId}
          onChange={(e) => setThreadId(e.target.value)}
          required
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
        />
      </div>

      <div className="flex justify-end gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={submitting}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {submitting ? 'Marking...' : 'Mark as Read'}
        </button>
      </div>
    </form>
  );
}

// Reply Form Component
function ReplyForm({
  onSubmit,
  onCancel,
}: {
  onSubmit: (data: { replyMessageId: string; originalMessageId: string; participantId: string; threadId: string }) => Promise<void>;
  onCancel: () => void;
}) {
  const [replyMessageId, setReplyMessageId] = useState('');
  const [originalMessageId, setOriginalMessageId] = useState('');
  const [participantId, setParticipantId] = useState('');
  const [threadId, setThreadId] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await onSubmit({
        replyMessageId: replyMessageId.trim(),
        originalMessageId: originalMessageId.trim(),
        participantId: participantId.trim(),
        threadId: threadId.trim(),
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="p-6 space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Reply Message ID <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={replyMessageId}
          onChange={(e) => setReplyMessageId(e.target.value)}
          required
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Original Message ID <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={originalMessageId}
          onChange={(e) => setOriginalMessageId(e.target.value)}
          required
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Participant ID <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={participantId}
          onChange={(e) => setParticipantId(e.target.value)}
          required
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Thread ID <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={threadId}
          onChange={(e) => setThreadId(e.target.value)}
          required
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
        />
      </div>

      <div className="flex justify-end gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={submitting}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {submitting ? 'Adding...' : 'Add Reply'}
        </button>
      </div>
    </form>
  );
}

